import React, { useMemo, useState } from "react";
import {
	CCBadge,
	CCButton,
	CCCombobox,
	CCComboboxOption,
	CCModal,
	CCSpinner,
} from "@pymblecitizencoders/cc-ui";
/* lucide — the outline family cc-ui uses. See EventCreateStart for why these
   are imported from the package rather than through CCIcons. */
import { CalendarCheck, Sparkles, SquarePen, X } from "lucide-react";
import { EventModalHead } from "./EventModalHead";
import { EventAudienceChips } from "./EventAudienceChips";
import { EventAssistantPanel } from "./EventAssistantPanel";
import { AudienceKey, Invitee } from "../../hooks/useInviteePicker";
import { ChosenTime, TimeSlot } from "../../hooks/useEventScheduling";
import "./eventManager.css";

interface EventCreateWizardProps {
	open: boolean;
	saving: boolean;
	/**
	 * A slot card's button in the assistant panel. Local-naive ISO strings.
	 *
	 * The panel holds no state the wizard needs: ChatKit owns the transcript and
	 * the thread, and the server draws the cards. All that crosses back is which
	 * button somebody pressed.
	 */
	onUseSlot: (start: string, end: string) => void;

	// Step 1 — the invitee picker, owned by useInviteePicker in the parent.
	/* No `term`: the combobox holds the typed text itself and reports it through
	   setTerm. Passing it back in would be two owners of one string. */
	setTerm: (value: string) => void;
	longEnough: boolean;
	/** False until an audience chip is on — the search needs a scope. */
	hasAudience: boolean;
	minQueryLength: number;
	audiences: Set<AudienceKey>;
	onToggleAudience: (key: AudienceKey) => void;
	results: Invitee[];
	searching: boolean;
	searchFailed: boolean;
	picked: Invitee[];
	pickedIds: Set<string>;
	onTogglePick: (invitee: Invitee) => void;
	onRemovePick: (id: string) => void;

	// Step 2 — suggested times.
	slots: TimeSlot[];
	slotsLoading: boolean;
	slotsError: string;
	inviteeCount: number;
	excludedCount: number;
	canLoadMore: boolean;
	selectedSlot: ChosenTime | null;
	onSelectSlot: (slot: TimeSlot) => void;
	onFindTimes: () => void;
	onMoreTimes: () => void;

	onClose: () => void;
	onManual: () => void;
	onContinue: () => void;
}

/** Tue / 21 — the date cube beside each suggestion. */
const dayParts = (iso: string) => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return { weekday: "", day: "" };
	return {
		weekday: d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase(),
		day: String(d.getDate()),
	};
};

const slotHeading = (slot: TimeSlot) => {
	const start = new Date(slot.Start);
	const end = new Date(slot.End);
	if (Number.isNaN(start.getTime())) return { date: "", time: "" };
	const t = (d: Date) =>
		d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
	return {
		date: start.toLocaleDateString("en-AU", {
			weekday: "short",
			day: "numeric",
			month: "short",
			year: "numeric",
		}),
		time: `${t(start)} – ${t(end)}`,
	};
};

/** One numbered step in the left column. */
const Step: React.FC<{
	n: number;
	title: string;
	blurb: string;
	done?: boolean;
	children?: React.ReactNode;
}> = ({ n, title, blurb, done, children }) => (
	<li className={`em-wiz__step${done ? " is-done" : ""}`}>
		<span className="em-wiz__num" aria-hidden="true">
			{n}
		</span>
		<div className="em-wiz__body">
			<h4 className="em-wiz__title">{title}</h4>
			<p className="em-muted">{blurb}</p>
			{children}
		</div>
	</li>
);

/**
 * The guided create flow: who is going, when, then create.
 *
 * All three steps are on screen at once rather than one at a time, which is why
 * this is not cc-ui's CCWizard — the point of the layout is that you can see
 * the time suggestions react as you change who is invited. CCWizard shows one
 * step per screen, which would hide exactly the thing that makes this useful.
 *
 * Step 2 is live against `POST event/admin/availability/`, specified in
 * my-pymble-portal `docs/architecture/event-scheduling/README.md`. The assistant
 * beside it reaches the same code by a different road: its `find_available_times`
 * tool calls the same `compute_availability`, and the server draws the result as
 * cards. So the two agree by construction rather than by care, and neither can
 * fabricate a suggestion — nothing on this screen renders a time that the
 * availability service did not return.
 *
 * ⚠️ PII. Search results are student and staff names. Component state only,
 * cleared when the dialog closes, never logged.
 */
export const EventCreateWizard: React.FC<EventCreateWizardProps> = ({
	open,
	saving,
	onUseSlot,
	setTerm,
	longEnough,
	hasAudience,
	minQueryLength,
	audiences,
	onToggleAudience,
	results,
	searching,
	searchFailed,
	picked,
	pickedIds,
	onTogglePick,
	onRemovePick,
	slots,
	slotsLoading,
	slotsError,
	inviteeCount,
	excludedCount,
	canLoadMore,
	selectedSlot,
	onSelectSlot,
	onFindTimes,
	onMoreTimes,
	onClose,
	onManual,
	onContinue,
}) => {
	const [showAllPicked, setShowAllPicked] = useState(false);

	const VISIBLE_CHIPS = 2;
	const shownPicked = showAllPicked ? picked : picked.slice(0, VISIBLE_CHIPS);
	const hiddenCount = picked.length - shownPicked.length;

	const status = useMemo(() => {
		if (selectedSlot) return { tone: "success" as const, text: "Time selected" };
		return { tone: "warning" as const, text: "Time not confirmed yet" };
	}, [selectedSlot]);

	/**
	 * Search results as combobox options.
	 *
	 * Keyed `kind:id` because a group and a contact are separate id spaces and
	 * nothing guarantees they never collide — the old list keyed its rows the same
	 * way.
	 *
	 * Anyone already picked is left OUT rather than shown ticked: with a dropdown
	 * that closes on choose, re-choosing someone would silently un-pick them and
	 * the panel would already be shut before it showed. Removing is the X on the
	 * chip below, which is where the picked people are.
	 */
	const options: CCComboboxOption[] = useMemo(
		() =>
			results
				.filter((r) => !pickedIds.has(r.id))
				.map((r) => ({
					value: `${r.kind}:${r.id}`,
					label: r.name,
					detail: r.kind === "group" ? "Group" : r.detail,
				})),
		[results, pickedIds]
	);

	/**
	 * What the empty dropdown says, which is four different situations.
	 *
	 * The combobox has one slot for this, so the wording has to carry the state:
	 * no audience chosen yet, a query too short to have run, a search that
	 * failed, and a search that ran and found nothing are not the same message.
	 * The first matters most — with no chip on, the search cannot run at all,
	 * and "Nothing matched" would blame the name rather than the missing filter.
	 */
	const emptyLabel = useMemo(() => {
		if (!hasAudience) return "Choose who you are looking for above";
		if (!longEnough) return `Type at least ${minQueryLength} characters`;
		if (searchFailed) return "Could not run that search";
		if (searching) return "Searching…";
		return picked.length && !results.length
			? "Nothing else matched"
			: "Nothing matched";
	}, [
		hasAudience,
		longEnough,
		minQueryLength,
		searchFailed,
		searching,
		picked.length,
		results.length,
	]);

	const pickFromSearch = (value: string) => {
		const chosen = results.find((r) => `${r.kind}:${r.id}` === value);
		if (chosen) onTogglePick(chosen);
	};

	return (
		<CCModal
			open={open}
			onClose={onClose}
			title="Create new event"
			className="em-wiz-modal"
			footer={
				/* Same split as the first screen: Cancel left, the ways forward
				   right. "Create manually instead" is a way forward, not a way
				   out, so it groups with Continue. */
				<div className="em-modal-footer">
					<CCButton variant="outline" onClick={onClose} disabled={saving}>
						Cancel
					</CCButton>
					<span className="em-modal-footer__right">
						<CCButton variant="outline" onClick={onManual} disabled={saving}>
							<SquarePen size={16} aria-hidden="true" /> Create manually
							instead
						</CCButton>
						<CCButton
							variant="solid"
							colour="primary"
							onClick={onContinue}
							disabled={saving || !picked.length}
						>
							Continue
						</CCButton>
					</span>
				</div>
			}
		>
			<div className="em-wiz">
				<EventModalHead
					title="Create new event"
					icon={CalendarCheck}
					status={status}
				/>

				<div className="em-wiz__cols">
					<ol className="em-wiz__steps">
						<Step
							n={1}
							title="Choose who is going"
							blurb="Select groups or people to attend."
							done={picked.length > 0}
						>
							{/* The chips filter the one search below rather than
							    opening four pickers — see useInviteePicker. */}
							<EventAudienceChips
								audiences={audiences}
								onToggle={onToggleAudience}
								className="em-wiz__chips"
							/>

							{/* The matches hang off the input as a dropdown, the way the
							    portal's navbar search does, rather than as a list pushing
							    the rest of the step down the modal. CCCombobox is the kit's
							    own version of that pattern, so the ARIA wiring, the arrow
							    keys, Enter to choose and Escape to dismiss come with it
							    instead of being re-implemented here.

							    `value` stays null on purpose: this is a token input, not a
							    single choice. Choosing commits to the chips below and the
							    combobox clears itself for the next name. */}
							<CCCombobox
								label="Search by name"
								placeholder="Name…"
								size="sm"
								fullWidth
								value={null}
								options={options}
								onChange={pickFromSearch}
								onQueryChange={setTerm}
								loading={searching}
								emptyLabel={emptyLabel}
								disabled={saving}
								error={
									searchFailed
										? "Could not run that search. Please try again."
										: undefined
								}
								hint={
									picked.length
										? undefined
										: "Search groups and people, then choose to add them."
								}
							/>

							{picked.length > 0 && (
								<ul className="em-att-add__picked em-wiz__picked">
									{shownPicked.map((p) => (
										<li key={p.id}>
											<span>{p.name}</span>
											<button
												type="button"
												aria-label={`Remove ${p.name}`}
												onClick={() => onRemovePick(p.id)}
											>
												<X size={14} aria-hidden="true" />
											</button>
										</li>
									))}
									{hiddenCount > 0 && (
										<li>
											<button
												type="button"
												className="em-wiz__more"
												onClick={() => setShowAllPicked(true)}
											>
												+{hiddenCount} more
											</button>
										</li>
									)}
								</ul>
							)}

						</Step>

						<Step
							n={2}
							title="Find a suitable time"
							blurb="Let AI suggest the best times based on availability."
							done={!!selectedSlot}
						>
							{slotsError ? (
								<p className="em-error" role="alert">
									{slotsError}
								</p>
							) : slotsLoading && !slots.length ? (
								<p className="em-muted">
									<CCSpinner size="inline" /> Checking availability…
								</p>
							) : !slots.length ? (
								<CCButton
									variant="outline"
									size="sm"
									disabled={!picked.length}
									onClick={onFindTimes}
								>
									<Sparkles size={16} aria-hidden="true" /> Suggest
									times
								</CCButton>
							) : (
								<>
									<p className="em-wiz__found">
										<Sparkles size={14} aria-hidden="true" /> Found{" "}
										{slots.length} recommended time slots
										{inviteeCount > 0 && (
											<span className="em-muted">
												{" "}
												across {inviteeCount}{" "}
												{inviteeCount === 1 ? "person" : "people"}
											</span>
										)}
									</p>
									{/* Carers have no timetable, so the API leaves them out of
									    the maths. Said out loud, because "62 people" and "62 of
									    80 people" are different claims. */}
									{excludedCount > 0 && (
										<p className="em-muted">
											{excludedCount} not counted — carers have no timetable
											to check.
										</p>
									)}
									<ul className="em-wiz__slots" role="radiogroup" aria-label="Suggested times">
										{slots.map((s, i) => {
											const { weekday, day } = dayParts(s.Start);
											const head = slotHeading(s);
											const chosen = selectedSlot?.Start === s.Start;
											return (
												<li key={s.Start}>
													<button
														type="button"
														role="radio"
														aria-checked={chosen}
														className={`em-wiz__slot${chosen ? " is-chosen" : ""}`}
														onClick={() => onSelectSlot(s)}
													>
														<span className="em-wiz__radio" aria-hidden="true" />
														<span className="em-wiz__cube" aria-hidden="true">
															<em>{weekday}</em>
															<strong>{day}</strong>
														</span>
														<span className="em-wiz__slot-main">
															<span className="em-wiz__slot-date">
																{head.date}
															</span>
															<span className="em-muted">{head.time}</span>
														</span>
														<span className="em-wiz__slot-meta">
															{/* The server's wording, not ours — see
															    TimeSlot.Label. */}
															<span>{s.Label}</span>
															{i === 0 && (
																<CCBadge tone="success">Best match</CCBadge>
															)}
															<span className="em-wiz__match">
																{s.MatchPercent}% match
															</span>
														</span>
													</button>
												</li>
											);
										})}
									</ul>
									{/* The API caps a page of suggestions; Truncated says more
									    exist, so this re-asks with a larger Limit rather than
									    paging — the list is short and ranked, so replacing it
									    with a longer one is what someone actually wants. */}
									{canLoadMore && (
										<CCButton
											variant="outline"
											size="sm"
											disabled={slotsLoading}
											onClick={onMoreTimes}
										>
											{slotsLoading ? "Loading…" : "See more times"}
										</CCButton>
									)}
								</>
							)}
						</Step>

						<Step
							n={3}
							title="Create event"
							blurb="Review details and finalise your event."
						/>
					</ol>

					{/* The assistant. It proposes people and asks the same
					    availability endpoint step 2 does — the times it finds land
					    in that list, not in a second one here. */}
					<EventAssistantPanel
						onUseSlot={onUseSlot}
						/* Adding, never toggling: the card's button says "Add to the
						   event", and pressing it twice must not quietly remove
						   somebody. Removing is the X on the chip, where the picked
						   people are. */
						onAddInvitee={(invitee) => {
							if (!pickedIds.has(invitee.id)) onTogglePick(invitee);
						}}
					/>
				</div>
			</div>
		</CCModal>
	);
};
